import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Select
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { paymentMethodService } from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const { Title } = Typography;

const PaymentMethodCreate = () => {
    const navigate = useNavigate();
    const { message } = App.useApp();
    const { setPageTitle, setRightContent } = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        type: [],
        pos_type: []
    });
    const [selectedType, setSelectedType] = useState(null);
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchFormData();
        setPageTitle('Создание способа оплаты');

        setRightContent(
            <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Создать
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    const fetchFormData = async () => {
        try {
            setLoading(true);
            // Получаем данные для формы
            const formDataResponse = await paymentMethodService.getFormData();

            if (formDataResponse.status === 'success') {
                setFormData(formDataResponse.data);
                console.log('Получены данные для формы:', formDataResponse.data);
            } else {
                message.error('Не удалось загрузить данные для формы');
                navigate('/payment-methods');
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных:', error);
            message.error('Ошибка при загрузке данных для формы');
            navigate('/payment-methods');
        } finally {
            setLoading(false);
        }
    };

    // Обработчик изменения типа способа оплаты
    const handleTypeChange = (value) => {
        setSelectedType(value);
        form.setFieldValue('type', value);

        // Если тип не card, очищаем поля POS
        if (value !== 'card') {
            form.setFieldsValue({
                pos_type: undefined,
                pos_ip_address: undefined
            });
        }
    };

    // Обработка сохранения данных способа оплаты
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await paymentMethodService.createPaymentMethod(
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Способ оплаты успешно создан');
                // Переходим на страницу созданного способа оплаты
                navigate(`/payment-methods/${response.data.payment_method.id}`);
            } else {
                message.error(response.message || 'Ошибка при создании способа оплаты');
            }
        } catch (error) {
            console.error('Ошибка при создании:', error);
            message.error(error.message || 'Ошибка при создании способа оплаты');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <Title level={3} style={{ marginTop: 0 }}>
                Создание нового способа оплаты
            </Title>

            <Form
                form={form}
                layout="horizontal"
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Основные атрибуты" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Название"
                                name="name"
                                rules={[
                                    { required: true, message: 'Введите название способа оплаты' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите название способа оплаты" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Внешний ID"
                                name="external_id"
                                rules={[
                                    { required: true, message: 'Введите внешний ID' }
                                ]}
                            >
                                <Input placeholder="Введите внешний ID" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Тип"
                                name="type"
                                rules={[
                                    { required: true, message: 'Выберите тип способа оплаты' }
                                ]}
                            >
                                <Select
                                    options={formData.type.map(type => ({
                                        label: type.label,
                                        value: type.value
                                    }))}
                                    placeholder="Выберите тип способа оплаты"
                                    showSearch
                                    onChange={handleTypeChange}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Тип POS"
                                name="pos_type"
                            >
                                <Select
                                    options={formData.pos_type.map(posType => ({
                                        label: posType.label,
                                        value: posType.value
                                    }))}
                                    placeholder="Выберите тип POS"
                                    showSearch
                                    disabled={selectedType !== 'card'}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="IP-адрес POS"
                                name="pos_ip_address"
                                rules={[
                                    {
                                        pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                                        message: 'Введите корректный IP-адрес'
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="Введите IP-адрес POS"
                                    disabled={selectedType !== 'card'}
                                />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default PaymentMethodCreate;